/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Product Offerings Selectors
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.10.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { IAuthRegion } from '../PageAuth/types/authTypes';
import { IMegaPower } from '../PageSubscriberId/components/Info/types';
import { IWizardProductOfferingsState } from './wizardProductOfferingsReducer';
import {
    IMegaPowersTotal,
    IProductOfferingsListItem,
    IProductOfferingsSubscriberActivationItem,
    IRatePlan,
    IService,
} from './wizardProductOfferingsTypes';

export const makeSelectProductOfferingsState = (state: IRootReducer): IWizardProductOfferingsState =>
    state.wizardProductOfferings;


export const makeSelectProductOfferingsRatePlanState = (): OutputSelector<
    IRootReducer,
    IRatePlan | undefined,
    (state: IWizardProductOfferingsState) => IRatePlan | undefined
> => createSelector(makeSelectProductOfferingsState, (state) => state.ratePlan);

export const makeSelectProductOfferingsServicesState = (): OutputSelector<
    IRootReducer,
    IService[] | undefined,
    (state: IWizardProductOfferingsState) => IService[] | undefined
> => createSelector(makeSelectProductOfferingsState, (state) => state.services);

// isStepValid
export const makeSelectProductOfferingsIsStepValid = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IWizardProductOfferingsState) => boolean
> => createSelector(makeSelectProductOfferingsState, (state) => state.isStepValid);

// validateError
export const makeSelectProductOfferingsValidateError = (): OutputSelector<
    IRootReducer,
    string,
    (state: IWizardProductOfferingsState) => string
> => createSelector(makeSelectProductOfferingsState, (state) => state.validateError);

// maxSubsForProductActivation
export const makeSelectProductOfferingsMaxSubsForProductActivation = (): OutputSelector<
    IRootReducer,
    number,
    (state: IWizardProductOfferingsState) => number
> => createSelector(makeSelectProductOfferingsState, (state) => state.maxSubsForProductActivation);

// subsCount
export const makeSelectProductOfferingsSubsCount = (): OutputSelector<
    IRootReducer,
    number,
    (state: IWizardProductOfferingsState) => number
> => createSelector(makeSelectProductOfferingsState, (state) => state.subsCount);

// subscriberActivationList
export const makeSelectProductOfferingsListToConnect = (): OutputSelector<
    IRootReducer,
    IProductOfferingsSubscriberActivationItem[],
    (state: IWizardProductOfferingsState) => IProductOfferingsSubscriberActivationItem[]
> => createSelector(makeSelectProductOfferingsState, (state) => state.subscriberActivationList);

// removedSubscribers
export const makeSelectProductOfferingsListRemovedSubscribers = (): OutputSelector<
    IRootReducer,
    string[],
    (state: IWizardProductOfferingsState) => string[]
> => createSelector(makeSelectProductOfferingsState, (state) => state.removedSubscribers);

// dateFrom
export const makeSelectProductOfferingsDateFrom = (): OutputSelector<
    IRootReducer,
    string,
    (state: IWizardProductOfferingsState) => string
> => createSelector(makeSelectProductOfferingsState, (state) => state.dateFrom);

// dateTo
export const makeSelectProductOfferingsDateTo = (): OutputSelector<
    IRootReducer,
    string,
    (state: IWizardProductOfferingsState) => string
> => createSelector(makeSelectProductOfferingsState, (state) => state.dateTo);

export const makeSelectProductOfferingsTimeFrom = (): OutputSelector<
    IRootReducer,
    string,
    (state: IWizardProductOfferingsState) => string
> => createSelector(makeSelectProductOfferingsState, (state) => state.timeFrom);

export const makeSelectProductOfferingsTimeTo = (): OutputSelector<
    IRootReducer,
    string,
    (state: IWizardProductOfferingsState) => string
> => createSelector(makeSelectProductOfferingsState, (state) => state.timeTo);

// totalMegaPowers
export const makeSelectProductOfferingsTotalMegaPowers = (): OutputSelector<
    IRootReducer,
    IMegaPowersTotal,
    (state: IWizardProductOfferingsState) => IMegaPowersTotal
> => createSelector(makeSelectProductOfferingsState, (state) => state.totalMegaPowers);

// initialMegaPowers
export const makeSelectProductOfferingsInitialMegaPowers = (): OutputSelector<
    IRootReducer,
    IMegaPower[],
    (state: IWizardProductOfferingsState) => IMegaPower[]
> => createSelector(makeSelectProductOfferingsState, (state) => state.initialMegaPowers);

// megaPowers
export const makeSelectProductOfferingsMegaPowers = (): OutputSelector<
    IRootReducer,
    IMegaPower[],
    (state: IWizardProductOfferingsState) => IMegaPower[]
> => createSelector(makeSelectProductOfferingsState, (state) => state.megaPowers);

// isTariff
export const makeSelectProductOfferingsIsTariff = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IWizardProductOfferingsState) => boolean
> => createSelector(makeSelectProductOfferingsState, (state) => state.isTariff);

// isDisconnect
export const makeSelectProductOfferingsIsDisconnect = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IWizardProductOfferingsState) => boolean
> => createSelector(makeSelectProductOfferingsState, (state) => state.isDisconnect);

// productOfferings
export const makeSelectProductOfferingsProductOfferings = (): OutputSelector<
    IRootReducer,
    IProductOfferingsListItem[],
    (state: IWizardProductOfferingsState) => IProductOfferingsListItem[]
> => createSelector(makeSelectProductOfferingsState, (state) => state.productOfferings);

// Region: если у абонента доступно несколько регионов, то выбирается region на основе regionId
export const makeSelectProductOfferingsRegion = (): OutputSelector<
    IRootReducer,
    IAuthRegion | undefined,
    (state: IWizardProductOfferingsState) => IAuthRegion | undefined
> => createSelector(makeSelectProductOfferingsState, (state) => state.region);

// RegionId: при старте визарда всегда указывается id региона абонента
export const makeSelectProductOfferingsRegionId = (): OutputSelector<
    IRootReducer,
    number,
    (state: IWizardProductOfferingsState) => number
> => createSelector(makeSelectProductOfferingsState, (state) => state.regionId);

export const makeSelectProductOfferingsRemovedSubscribers = (): OutputSelector<
    IRootReducer,
    string[],
    (state: IWizardProductOfferingsState) => string[]
> => createSelector(makeSelectProductOfferingsState, (state) => state.removedSubscribers || []);
